import {
    Alert,
    Button,
    Progress,
    Spin,
} from 'antd'

import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import fetch from 'auth/FetchInterceptor'

import {
    NetworkService,
    OfflineAssetService,
    OfflineGameService,
    OfflineQuizAttemptService,
} from 'offline'

import {
    env,
} from 'configs/EnvironmentConfig'

// ======================================================
// API
// ======================================================

const API_BASE_URL =
    env.API_ENDPOINT_URL

// ======================================================
// URL DE MÍDIA
// ======================================================

const getMediaUrl = (
    value,
) => {
    if (!value) {
        return ''
    }

    if (
        value.startsWith(
            'http://',
        ) ||
        value.startsWith(
            'https://',
        ) ||
        value.startsWith(
            'data:',
        ) ||
        value.startsWith(
            'blob:',
        )
    ) {
        return value
    }

    return `${API_BASE_URL}${value}`
}

// ======================================================
// COLETAR MÍDIAS DO QUIZ PARA USO OFFLINE
// ======================================================

const collectQuizMediaUrls = (
    config,
) => {
    if (!config) {
        return []
    }

    const urls =
        []

    const add =
        (
            value,
        ) => {
            if (
                typeof value !==
                'string' ||
                !value.trim()
            ) {
                return
            }

            const url =
                value.trim()

            /*
             * data: já contém o arquivo.
             * blob: é temporário.
             *
             * Nenhum dos dois precisa ser armazenado.
             */
            if (
                url.startsWith(
                    'data:',
                ) ||
                url.startsWith(
                    'blob:',
                )
            ) {
                return
            }

            urls.push(
                url,
            )
        }

    // ==================================================
    // TELA INICIAL
    // ==================================================

    add(
        config
            .startScreenBackgroundUrl,
    )

    add(
        config
            .startScreenBackgroundVideoUrl,
    )

    add(
        config
            .startScreenLogoUrl,
    )

    // ==================================================
    // PERGUNTAS
    // ==================================================

    add(
        config
            .questionBackgroundUrl,
    )

    add(
        config
            .questionBackgroundVideoUrl,
    )

    add(
        config
            .questionLogoUrl,
    )

    const questions =
        Array.isArray(
            config.questions,
        )
            ? config.questions
            : []

    questions.forEach(
        (
            question,
        ) => {
            add(
                question
                    ?.backgroundUrl,
            )
        },
    )

    // ==================================================
    // RESULTADO
    // ==================================================

    add(
        config
            .resultBackgroundUrl,
    )

    add(
        config
            .resultBackgroundVideoUrl,
    )

    add(
        config
            .resultLogoUrl,
    )

    const results =
        Array.isArray(
            config.results,
        )
            ? config.results
            : []

    results.forEach(
        (
            result,
        ) => {
            add(
                result
                    ?.imageUrl,
            )
        },
    )

    // ==================================================
    // REMOVER DUPLICADOS
    // ==================================================

    return Array.from(
        new Set(
            urls,
        ),
    )
}

// ======================================================
// RESOLVER MÍDIAS DO QUIZ PARA USO OFFLINE
// ======================================================

const resolveQuizOfflineMedia =
    async (
        config,
    ) => {
        if (!config) {
            return config
        }

        const resolved = {
            ...config,
        }

        // ==============================================
        // HELPER
        // ==============================================

        const resolve =
            async (
                value,
            ) => {
                if (!value) {
                    return value
                }

                const resolvedUrl =
                    await OfflineAssetService
                        .resolveUrl(
                            value,
                        )

                const isOffline =
                    String(
                        resolvedUrl ||
                        '',
                    ).startsWith(
                        'blob:',
                    ) ||
                    String(
                        resolvedUrl ||
                        '',
                    ).startsWith(
                        'data:',
                    )

                console.log(
                    '[QUIZ MEDIA] Mídia resolvida:',
                    {
                        original:
                            value,

                        resolved:
                            resolvedUrl,

                        offline:
                            isOffline,
                    },
                )

                return resolvedUrl ||
                    value
            }

        // ==============================================
        // START SCREEN
        // ==============================================

        resolved.startScreenBackgroundUrl =
            await resolve(
                config
                    .startScreenBackgroundUrl,
            )

        resolved.startScreenBackgroundVideoUrl =
            await resolve(
                config
                    .startScreenBackgroundVideoUrl,
            )

        resolved.startScreenLogoUrl =
            await resolve(
                config
                    .startScreenLogoUrl,
            )

        // ==============================================
        // QUESTION SCREEN
        // ==============================================

        resolved.questionBackgroundUrl =
            await resolve(
                config
                    .questionBackgroundUrl,
            )

        resolved.questionBackgroundVideoUrl =
            await resolve(
                config
                    .questionBackgroundVideoUrl,
            )

        resolved.questionLogoUrl =
            await resolve(
                config
                    .questionLogoUrl,
            )

        // ==============================================
        // PERGUNTAS
        // ==============================================

        if (
            Array.isArray(
                config.questions,
            )
        ) {
            resolved.questions =
                await Promise.all(
                    config.questions.map(
                        async (
                            question,
                        ) => {
                            return {
                                ...question,

                                backgroundUrl:
                                    await resolve(
                                        question
                                            ?.backgroundUrl,
                                    ),
                            }
                        },
                    ),
                )
        }

        // ==============================================
        // RESULT SCREEN
        // ==============================================

        resolved.resultBackgroundUrl =
            await resolve(
                config
                    .resultBackgroundUrl,
            )

        resolved.resultBackgroundVideoUrl =
            await resolve(
                config
                    .resultBackgroundVideoUrl,
            )

        resolved.resultLogoUrl =
            await resolve(
                config
                    .resultLogoUrl,
            )

        // ==============================================
        // PERFIS DE RESULTADO
        // ==============================================

        if (
            Array.isArray(
                config.results,
            )
        ) {
            resolved.results =
                await Promise.all(
                    config.results.map(
                        async (
                            result,
                        ) => {
                            return {
                                ...result,

                                imageUrl:
                                    await resolve(
                                        result
                                            ?.imageUrl,
                                    ),
                            }
                        },
                    ),
                )
        }

        return resolved
    }

// ======================================================
// POSIÇÃO
// ======================================================

const getPositionStyle = (
    position,
) => {
    switch (
    position
    ) {
        case 'RIGHT':
            return {
                marginLeft:
                    'auto',
                marginRight:
                    '5%',
            }

        case 'CENTER':
            return {
                marginLeft:
                    'auto',
                marginRight:
                    'auto',
            }

        case 'LEFT':
        default:
            return {
                marginLeft:
                    '5%',
                marginRight:
                    'auto',
            }
    }
}

// ======================================================
// START SCREEN - POSICIONAMENTO
// ======================================================

const getHorizontalAlignment = (
    value,
) => {
    switch (value) {
        case 'RIGHT':
            return {
                justifyContent:
                    'flex-end',
                textAlign:
                    'right',
            }

        case 'CENTER':
            return {
                justifyContent:
                    'center',
                textAlign:
                    'center',
            }

        case 'LEFT':
        default:
            return {
                justifyContent:
                    'flex-start',
                textAlign:
                    'left',
            }
    }
}

const getVerticalAlignment = (
    value,
) => {
    switch (value) {
        case 'TOP':
            return 'flex-start'

        case 'BOTTOM':
            return 'flex-end'

        case 'CENTER':
        default:
            return 'center'
    }
}

const getButtonAlignment = (
    value,
) => {
    switch (value) {
        case 'RIGHT':
            return 'flex-end'

        case 'CENTER':
            return 'center'

        case 'LEFT':
        default:
            return 'flex-start'
    }
}

// ======================================================
// FUNDO
// ======================================================

const ScreenBackground = ({
    mediaType,
    imageUrl,
    videoUrl,
    backgroundColor,
}) => {
    const image =
        getMediaUrl(
            imageUrl,
        )

    const video =
        getMediaUrl(
            videoUrl,
        )

    if (
        mediaType ===
        'VIDEO' &&
        video
    ) {
        return (
            <video
                src={video}
                autoPlay
                muted
                loop
                playsInline
                style={{
                    position:
                        'absolute',
                    inset:
                        0,
                    width:
                        '100%',
                    height:
                        '100%',
                    objectFit:
                        'cover',
                }}
            />
        )
    }

    if (image) {
        return (
            <div
                style={{
                    position:
                        'absolute',
                    inset:
                        0,

                    backgroundImage:
                        `url("${image}")`,

                    backgroundSize:
                        'cover',

                    backgroundPosition:
                        'center',

                    backgroundRepeat:
                        'no-repeat',
                }}
            />
        )
    }

    return (
        <div
            style={{
                position:
                    'absolute',
                inset:
                    0,

                background:
                    backgroundColor ||
                    '#000000',
            }}
        />
    )
}

// ======================================================
// OVERLAY
// ======================================================

const ScreenOverlay = ({
    enabled,
    color,
}) => {
    if (!enabled) {
        return null
    }

    return (
        <div
            style={{
                position:
                    'absolute',
                inset:
                    0,

                background:
                    color ||
                    'rgba(0,0,0,0.25)',

                pointerEvents:
                    'none',
            }}
        />
    )
}

// ======================================================
// LOGO
// ======================================================

const QuizLogo = ({
    show,
    url,
    width,
}) => {
    if (
        !show ||
        !url
    ) {
        return null
    }

    return (
        <img
            src={
                getMediaUrl(
                    url,
                )
            }
            alt=""
            draggable={
                false
            }
            style={{
                display:
                    'block',

                width:
                    width ||
                    220,

                maxWidth:
                    '80%',

                height:
                    'auto',

                objectFit:
                    'contain',

                margin:
                    '0 auto 30px',
            }}
        />
    )
}

// ======================================================
// QUIZ
// ======================================================

const QuizGame = ({
    game,
    leadId,
    leadValues,
    onGameFinished,
}) => {
    // ==================================================
    // ESTADO
    // ==================================================

    const [
        loading,
        setLoading,
    ] = useState(true)

    const [
        submitting,
        setSubmitting,
    ] = useState(false)

    const [
        error,
        setError,
    ] = useState('')

    const [
        config,
        setConfig,
    ] = useState(null)

    const [
        screen,
        setScreen,
    ] = useState(
        'START',
    )

    const [
        attempt,
        setAttempt,
    ] = useState(null)

    const [
        questionIndex,
        setQuestionIndex,
    ] = useState(0)

    const [
        selectedOptionId,
        setSelectedOptionId,
    ] = useState(null)

    const [
        result,
        setResult,
    ] = useState(null)

    // ==================================================
    // TRAVAS DE EXECUÇÃO
    // ==================================================

    const startingRef =
        useRef(false)

    const answeringRef =
        useRef(false)

    const advanceTimerRef =
        useRef(null)

    // ==================================================
    // LIMPEZA DOS TIMERS
    // ==================================================

    useEffect(
        () => {
            return () => {
                if (
                    advanceTimerRef.current
                ) {
                    window.clearTimeout(
                        advanceTimerRef.current,
                    )
                }

                advanceTimerRef.current =
                    null

                answeringRef.current =
                    false

                startingRef.current =
                    false
            }
        },
        [],
    )

    // ==================================================
    // CARREGAR CONFIGURAÇÃO
    // ONLINE + OFFLINE
    // ==================================================

    const loadConfig =
        useCallback(
            async () => {
                if (
                    !game?.id
                ) {
                    return
                }

                try {
                    setLoading(
                        true,
                    )

                    setError(
                        '',
                    )

                    let loadedConfig =
                        null

                    // ==========================================
                    // 1. TENTAR CARREGAR ONLINE
                    // ==========================================

                    try {
                        const response =
                            await fetch({
                                url:
                                    `/quiz-games/${game.id}`,

                                method:
                                    'get',

                                silentNetworkError:
                                    true,
                            })

                        const payload =
                            response?.data ||
                            response

                        loadedConfig =
                            payload?.config ||
                            payload

                        if (
                            loadedConfig
                        ) {
                            // ==================================
                            // SALVAR SNAPSHOT PARA USO OFFLINE
                            // ==================================

                            await OfflineGameService
                                .saveQuizConfig(
                                    game.id,
                                    loadedConfig,
                                )
                            // ======================================
                            // PREPARAR MÍDIAS PARA USO OFFLINE
                            // ======================================

                            const mediaUrls =
                                collectQuizMediaUrls(
                                    loadedConfig,
                                )

                            console.log(
                                '[QUIZ OFFLINE] Preparando mídias:',
                                {
                                    gameId:
                                        game.id,

                                    total:
                                        mediaUrls
                                            .length,

                                    urls:
                                        mediaUrls,
                                },
                            )

                            const mediaPreparation =
                                await OfflineAssetService
                                    .cacheAssets(
                                        game.id,
                                        mediaUrls,
                                    )

                            console.log(
                                '[QUIZ OFFLINE] Mídias preparadas:',
                                mediaPreparation,
                            )

                            console.log(
                                '[QUIZ] Configuração online carregada e armazenada offline.',
                            )
                        }
                    } catch (
                    onlineError
                    ) {
                        console.warn(
                            '[QUIZ] API indisponível. Tentando configuração offline.',
                            onlineError,
                        )
                    }

                    // ==========================================
                    // 2. FALLBACK OFFLINE
                    // ==========================================

                    if (
                        !loadedConfig
                    ) {
                        loadedConfig =
                            await OfflineGameService
                                .getQuizConfig(
                                    game.id,
                                )

                        if (
                            loadedConfig
                        ) {
                            console.log(
                                '[QUIZ OFFLINE] Configuração carregada do cache.',
                            )
                        }
                    }

                    // ==========================================
                    // 3. NÃO EXISTE ONLINE NEM OFFLINE
                    // ==========================================

                    if (
                        !loadedConfig
                    ) {
                        throw new Error(
                            'QUIZ_CONFIG_NOT_AVAILABLE',
                        )
                    }

                    // ==========================================
                    // 3.1 RESOLVER MÍDIAS DO CACHE OFFLINE
                    // ==========================================

                    if (
                        loadedConfig
                    ) {
                        loadedConfig =
                            await resolveQuizOfflineMedia(
                                loadedConfig,
                            )

                        console.log(
                            '[QUIZ OFFLINE] Configuração com mídias locais preparada.',
                        )
                    }

                    // ==========================================
                    // 4. APLICAR CONFIGURAÇÃO
                    // ==========================================

                    setConfig(
                        loadedConfig,
                    )

                    if (
                        loadedConfig
                            .startScreenEnabled ===
                        false
                    ) {
                        setScreen(
                            'QUESTION',
                        )
                    } else {
                        setScreen(
                            'START',
                        )
                    }
                } catch (
                requestError
                ) {
                    console.error(
                        '[QUIZ] Erro ao carregar:',
                        requestError,
                    )

                    setConfig(
                        null,
                    )

                    setError(
                        'Quiz indisponível. Conecte este equipamento à internet pelo menos uma vez para preparar o jogo offline.',
                    )
                } finally {
                    setLoading(
                        false,
                    )
                }
            },
            [
                game?.id,
            ],
        )

    useEffect(
        () => {
            loadConfig()
        },
        [
            loadConfig,
        ],
    )

    // ==================================================
    // PERGUNTAS
    // ==================================================

    const questions =
        useMemo(
            () => {
                const source =
                    Array.isArray(
                        config?.questions,
                    )
                        ? config.questions
                        : []

                return source
                    .filter(
                        (
                            question,
                        ) =>
                            question
                                ?.isActive !==
                            false,
                    )
                    .sort(
                        (
                            a,
                            b,
                        ) =>
                            Number(
                                a.sortOrder ||
                                0,
                            ) -
                            Number(
                                b.sortOrder ||
                                0,
                            ),
                    )
            },
            [
                config,
            ],
        )

    const currentQuestion =
        questions[
        questionIndex
        ]

    const options =
        useMemo(
            () => {
                const source =
                    Array.isArray(
                        currentQuestion
                            ?.options,
                    )
                        ? currentQuestion.options
                        : []

                return source
                    .filter(
                        (
                            option,
                        ) =>
                            option
                                ?.isActive !==
                            false,
                    )
                    .sort(
                        (
                            a,
                            b,
                        ) =>
                            Number(
                                a.sortOrder ||
                                0,
                            ) -
                            Number(
                                b.sortOrder ||
                                0,
                            ),
                    )
            },
            [
                currentQuestion,
            ],
        )

    // ==================================================
    // PARTICIPANTE
    // ==================================================

    const participant =
        useMemo(
            () => {
                if (
                    !leadValues
                ) {
                    return null
                }

                return {
                    name:
                        leadValues.nome ||
                        leadValues.name ||
                        null,

                    email:
                        leadValues.email ||
                        null,

                    phone:
                        leadValues.telefone ||
                        leadValues.phone ||
                        null,

                    cpf:
                        leadValues.cpf ||
                        null,

                    company:
                        leadValues.empresa ||
                        leadValues.company ||
                        null,

                    role:
                        leadValues.cargo ||
                        leadValues.role ||
                        null,

                    lgpdAccepted:
                        Boolean(
                            leadValues
                                .lgpdAccepted,
                        ),
                }
            },
            [
                leadValues,
            ],
        )

    // ==================================================
    // CRIAR TENTATIVA
    // ONLINE + OFFLINE
    // ==================================================

    const createAttempt =
        async () => {
            // ==========================================
            // DADOS DA TENTATIVA
            // ==========================================

            const attemptData = {
                gameId:
                    game.id,

                leadId:
                    leadId ||
                    null,

                participant:
                    participant ||
                    null,
            }

            // ==========================================
            // 1. TENTAR ONLINE
            // ==========================================

            try {
                const response =
                    await fetch({
                        url:
                            `/quiz-games/${game.id}/attempts`,

                        method:
                            'post',

                        silentNetworkError:
                            true,

                        data: {
                            leadId:
                                leadId ||
                                null,

                            participant:
                                participant ||
                                null,
                        },
                    })

                const created =
                    response?.data ||
                    response

                if (
                    created?.id
                ) {
                    console.log(
                        '[QUIZ] Tentativa online criada:',
                        created.id,
                    )

                    return {
                        ...created,

                        offline:
                            false,
                    }
                }

                throw new Error(
                    'QUIZ_ATTEMPT_INVALID_RESPONSE',
                )
            } catch (
            onlineError
            ) {
                // ======================================
                // ERROS HTTP NÃO DEVEM VIRAR OFFLINE
                // ======================================

                const status =
                    onlineError
                        ?.response
                        ?.status

                if (
                    status &&
                    status >= 400 &&
                    status < 500
                ) {
                    console.error(
                        '[QUIZ] API recusou a tentativa:',
                        onlineError,
                    )

                    throw onlineError
                }

                console.warn(
                    '[QUIZ OFFLINE] API indisponível. Criando tentativa local.',
                    onlineError,
                )
            }

            // ==========================================
            // 2. CRIAR TENTATIVA OFFLINE
            // ==========================================

            const offlineAttempt =
                await OfflineQuizAttemptService
                    .createAttempt(
                        attemptData,
                    )

            if (
                !offlineAttempt?.id
            ) {
                throw new Error(
                    'QUIZ_OFFLINE_ATTEMPT_NOT_CREATED',
                )
            }

            console.log(
                '[QUIZ OFFLINE] Tentativa local criada:',
                offlineAttempt.id,
            )

            return {
                ...offlineAttempt,

                offline:
                    true,
            }
        }

    // ==================================================
    // COMEÇAR
    // ==================================================

    const handleStart =
        async () => {
            if (
                startingRef.current ||
                submitting
            ) {
                return
            }

            startingRef.current =
                true

            try {
                setSubmitting(
                    true,
                )

                setError(
                    '',
                )

                // ==========================================
                // LIMPAR QUALQUER JOGO ANTERIOR
                // ==========================================

                if (
                    advanceTimerRef.current
                ) {
                    window.clearTimeout(
                        advanceTimerRef.current,
                    )

                    advanceTimerRef.current =
                        null
                }

                answeringRef.current =
                    false

                setAttempt(
                    null,
                )

                setResult(
                    null,
                )

                setQuestionIndex(
                    0,
                )

                setSelectedOptionId(
                    null,
                )

                // ==========================================
                // SEMPRE CRIAR UMA NOVA TENTATIVA
                // ==========================================

                const created =
                    await createAttempt()

                if (
                    !created?.id
                ) {
                    throw new Error(
                        'A API não retornou o ID da tentativa.',
                    )
                }

                console.log(
                    '[QUIZ] Nova tentativa criada:',
                    created.id,
                )

                setAttempt(
                    created,
                )

                setQuestionIndex(
                    0,
                )

                setSelectedOptionId(
                    null,
                )

                setScreen(
                    'QUESTION',
                )
            } catch (
            requestError
            ) {
                console.error(
                    '[QUIZ] Erro ao iniciar:',
                    requestError,
                )

                setAttempt(
                    null,
                )

                setError(
                    requestError
                        ?.response
                        ?.data
                        ?.message ||
                    requestError
                        ?.message ||
                    'Não foi possível iniciar o Quiz.',
                )
            } finally {
                startingRef.current =
                    false

                setSubmitting(
                    false,
                )
            }
        }

    // ==================================================
    // GARANTIR TENTATIVA SEM START SCREEN
    // ==================================================

    useEffect(
        () => {
            if (
                loading ||
                !config ||
                config
                    .startScreenEnabled !==
                false ||
                attempt
            ) {
                return
            }

            handleStart()
            // eslint-disable-next-line
        },
        [
            loading,
            config,
            attempt,
        ],
    )

    // ======================================================
    // FINALIZAR TENTATIVA
    // ONLINE + OFFLINE
    // ======================================================

    const finishAttempt =
        async (
            attemptId,
        ) => {
            const isOfflineAttempt =
                String(
                    attemptId,
                ).startsWith(
                    'offline-quiz-',
                )

            // ==================================================
            // ONLINE
            // ==================================================

            if (
                !isOfflineAttempt
            ) {
                try {
                    // ==========================================
                    // FINALIZAR ONLINE
                    // ==========================================

                    const response =
                        await fetch({
                            url:
                                `/quiz-games/${game.id}/attempts/${attemptId}/finish`,

                            method:
                                'post',

                            silentNetworkError:
                                true,
                        })

                    const payload =
                        response?.data ||
                        response

                    setResult(
                        payload,
                    )

                    setAttempt(
                        payload,
                    )

                    setScreen(
                        'RESULT',
                    )

                    return payload
                } catch (
                onlineFinishError
                ) {
                    // ==========================================
                    // ERRO HTTP NAO DEVE VIRAR OFFLINE
                    // ==========================================

                    const status =
                        onlineFinishError
                            ?.response
                            ?.status

                    if (
                        status &&
                        status >=
                        400 &&
                        status <
                        500
                    ) {
                        throw onlineFinishError
                    }

                    console.warn(
                        '[QUIZ OFFLINE] Conexao perdida ao finalizar. Finalizando localmente.',
                        onlineFinishError,
                    )

                    // ==========================================
                    // RECUPERAR ESTADO ATUAL DA TENTATIVA
                    // ==========================================

                    const currentOnlineAttempt =
                        attempt &&
                            String(
                                attempt.id,
                            ) ===
                            String(
                                attemptId,
                            )
                            ? attempt
                            : null

                    if (
                        !currentOnlineAttempt
                    ) {
                        throw new Error(
                            'QUIZ_OFFLINE_FINISH_ATTEMPT_NOT_FOUND',
                        )
                    }

                    const mirroredAnswers =
                        Array.isArray(
                            currentOnlineAttempt
                                .answers,
                        )
                            ? currentOnlineAttempt
                                .answers
                            : []

                    if (
                        mirroredAnswers.length ===
                        0
                    ) {
                        throw new Error(
                            'QUIZ_OFFLINE_FINISH_WITHOUT_ANSWERS',
                        )
                    }

                    // ==========================================
                    // ADOTAR A TENTATIVA ONLINE
                    // ==========================================

                    const adoptedAttempt =
                        await OfflineQuizAttemptService
                            .adoptOnlineAttempt({
                                serverAttemptId:
                                    attemptId,

                                gameId:
                                    game.id,

                                leadId:
                                    currentOnlineAttempt
                                        .leadId ||
                                    null,

                                participant:
                                    currentOnlineAttempt
                                        .participant ||
                                    participant ||
                                    null,

                                answers:
                                    mirroredAnswers,

                                totalScore:
                                    currentOnlineAttempt
                                        .totalScore ||
                                    0,

                                answeredQuestions:
                                    currentOnlineAttempt
                                        .answeredQuestions ||
                                    mirroredAnswers
                                        .length,

                                startedAt:
                                    currentOnlineAttempt
                                        .startedAt ||
                                    null,
                            })

                    if (
                        !adoptedAttempt?.id
                    ) {
                        throw new Error(
                            'QUIZ_OFFLINE_FINISH_ADOPTION_FAILED',
                        )
                    }

                    console.log(
                        '[QUIZ OFFLINE] Tentativa adotada durante finalizacao:',
                        {
                            localAttemptId:
                                adoptedAttempt.id,

                            serverAttemptId:
                                adoptedAttempt
                                    .serverAttemptId,

                            answers:
                                adoptedAttempt
                                    .answers
                                    ?.length ||
                                0,
                        },
                    )

                    // ==========================================
                    // USAR A FINALIZACAO OFFLINE
                    // ==========================================

                    return finishAttempt(
                        adoptedAttempt.id,
                    )
                }
            }

            // ==================================================
            // OFFLINE
            // ==================================================

            console.log(
                '[QUIZ OFFLINE] Finalizando tentativa:',
                attemptId,
            )

            const localAttempt =
                await OfflineQuizAttemptService
                    .getAttemptById(
                        attemptId,
                    )

            if (
                !localAttempt
            ) {
                throw new Error(
                    'Tentativa offline não encontrada.',
                )
            }

            // ==================================================
            // CALCULAR PONTUAÇÃO
            // ==================================================

            const answers =
                Array.isArray(
                    localAttempt.answers,
                )
                    ? localAttempt.answers
                    : []

            const totalScore =
                answers.reduce(
                    (
                        total,
                        answer,
                    ) => {
                        return (
                            total +
                            Number(
                                answer.points ||
                                0,
                            )
                        )
                    },
                    0,
                )

            // ==================================================
            // LOCALIZAR PERFIL DE RESULTADO
            //
            // Mesma regra da API:
            //
            // minScore <= totalScore
            // maxScore >= totalScore
            // isActive = true
            // ==================================================

            const resultRanges =
                Array.isArray(
                    config?.results,
                )
                    ? config.results
                    : []

            const activeResultRanges =
                resultRanges
                    .filter(
                        (
                            range,
                        ) =>
                            range?.isActive !==
                            false,
                    )
                    .sort(
                        (
                            a,
                            b,
                        ) =>
                            Number(
                                a?.sortOrder ||
                                0,
                            ) -
                            Number(
                                b?.sortOrder ||
                                0,
                            ),
                    )

            let resultRange =
                null

            if (
                config?.mode ===
                'SCORE_PROFILE'
            ) {
                resultRange =
                    activeResultRanges.find(
                        (
                            range,
                        ) => {
                            const minScore =
                                Number(
                                    range
                                        ?.minScore,
                                )

                            const maxScore =
                                Number(
                                    range
                                        ?.maxScore,
                                )

                            return (
                                Number
                                    .isFinite(
                                        minScore,
                                    ) &&
                                Number
                                    .isFinite(
                                        maxScore,
                                    ) &&
                                minScore <=
                                totalScore &&
                                maxScore >=
                                totalScore
                            )
                        },
                    ) ||
                    null

                if (
                    !resultRange
                ) {
                    throw new Error(
                        `Nenhum perfil de resultado configurado para ${totalScore} pontos.`,
                    )
                }
            }

            // ==================================================
            // FINALIZAR LOCALMENTE
            // ==================================================

            const finishedAttempt =
                await OfflineQuizAttemptService
                    .finishAttempt({
                        attemptId,

                        resultRange,
                    })

            // ==================================================
            // NORMALIZAR IGUAL AO RETORNO DA API
            // ==================================================

            const payload = {
                ...finishedAttempt,

                totalScore,

                answeredQuestions:
                    answers.length,

                status:
                    'COMPLETED',

                resultRange,

                answers,

                offline:
                    true,
            }

            // ==================================================
            // ATUALIZAR TENTATIVA LOCAL
            // ==================================================

            await OfflineQuizAttemptService
                .updateAttempt(
                    attemptId,
                    {
                        totalScore,

                        answeredQuestions:
                            answers.length,

                        status:
                            'COMPLETED',

                        resultRange,

                        finishedAt:
                            new Date()
                                .toISOString(),

                        syncStatus:
                            'PENDING',
                    },
                )

            console.log(
                '[QUIZ OFFLINE] Tentativa finalizada:',
                {
                    attemptId,

                    totalScore,

                    answeredQuestions:
                        answers.length,

                    resultRange:
                        resultRange
                            ?.title ||
                        null,
                },
            )

            setResult(
                payload,
            )

            setAttempt(
                payload,
            )

            setScreen(
                'RESULT',
            )

            return payload
        }

    // ==================================================
    // RESPONDER
    // ==================================================

    const handleAnswer =
        async (
            option,
        ) => {
            // ==========================================
            // TRAVA SÍNCRONA
            // ==========================================

            if (
                answeringRef.current ||
                submitting ||
                !currentQuestion ||
                !option?.id
            ) {
                return
            }

            answeringRef.current =
                true

            try {
                setSubmitting(
                    true,
                )

                setError(
                    '',
                )

                setSelectedOptionId(
                    option.id,
                )

                // ==========================================
                // A TENTATIVA PRECISA EXISTIR
                // ==========================================

                let currentAttempt =
                    attempt

                if (
                    !currentAttempt?.id
                ) {
                    console.warn(
                        '[QUIZ] Tentativa ausente. Criando nova tentativa.',
                    )

                    currentAttempt =
                        await createAttempt()

                    if (
                        !currentAttempt?.id
                    ) {
                        throw new Error(
                            'Não foi possível criar uma nova tentativa.',
                        )
                    }

                    setAttempt(
                        currentAttempt,
                    )
                }

                const questionId =
                    currentQuestion.id

                const optionId =
                    option.id

                console.log(
                    '[QUIZ] Enviando resposta:',
                    {
                        attemptId:
                            currentAttempt.id,
                        questionId,
                        optionId,
                        questionIndex,
                    },
                )

                // ==========================================
                // REGISTRAR RESPOSTA
                // ==========================================

                // ==========================================
                // REGISTRAR RESPOSTA
                // ONLINE + OFFLINE
                // ==========================================

                const isOfflineAttempt =
                    String(
                        currentAttempt.id,
                    ).startsWith(
                        'offline-quiz-',
                    ) ||
                    currentAttempt
                        .offline ===
                    true

                if (
                    isOfflineAttempt
                ) {
                    // ======================================
                    // TENTATIVA OFFLINE
                    // ======================================

                    const optionPoints =
                        Number(
                            option.points ||
                            option.score ||
                            option.value ||
                            0,
                        )

                    const updatedAttempt =
                        await OfflineQuizAttemptService
                            .saveAnswer({
                                attemptId:
                                    currentAttempt.id,

                                questionId,

                                optionId,

                                points:
                                    optionPoints,
                            })

                    currentAttempt = {
                        ...updatedAttempt,

                        offline:
                            true,
                    }

                    setAttempt(
                        currentAttempt,
                    )

                    console.log(
                        '[QUIZ OFFLINE] Resposta salva:',
                        {
                            attemptId:
                                currentAttempt.id,

                            questionId,

                            optionId,

                            points:
                                optionPoints,

                            totalScore:
                                currentAttempt
                                    .totalScore,
                        },
                    )
                } else {
                    // ======================================
                    // TENTATIVA ONLINE
                    // ======================================

                    try {
                        const response =
                            await fetch({
                                url:
                                    `/quiz-games/${game.id}/attempts/${currentAttempt.id}/answers`,

                                method:
                                    'post',

                                silentNetworkError:
                                    true,

                                data: {
                                    questionId,

                                    optionId,
                                },
                            })

                        const responsePayload =
                            response?.data ||
                            response ||
                            {}

                        // ==================================
                        // ESPELHO LOCAL DA RESPOSTA ONLINE
                        //
                        // Mantemos as respostas no estado
                        // mesmo enquanto a API funciona.
                        // Isso permite continuar offline
                        // caso a conexão caia depois.
                        // ==================================

                        const optionPoints =
                            Number(
                                responsePayload
                                    ?.answer
                                    ?.points ??
                                option.points ??
                                option.score ??
                                option.value ??
                                0,
                            )

                        const existingAnswers =
                            Array.isArray(
                                currentAttempt
                                    .answers,
                            )
                                ? currentAttempt
                                    .answers
                                : []

                        const mirroredAnswer = {
                            questionId,

                            optionId,

                            points:
                                optionPoints,

                            answeredAt:
                                responsePayload
                                    ?.answer
                                    ?.answeredAt ||
                                new Date()
                                    .toISOString(),
                        }

                        const answerIndex =
                            existingAnswers
                                .findIndex(
                                    (
                                        answer,
                                    ) =>
                                        answer
                                            .questionId ===
                                        questionId,
                                )

                        let nextAnswers

                        if (
                            answerIndex >=
                            0
                        ) {
                            nextAnswers = [
                                ...existingAnswers,
                            ]

                            nextAnswers[
                                answerIndex
                            ] = mirroredAnswer
                        } else {
                            nextAnswers = [
                                ...existingAnswers,
                                mirroredAnswer,
                            ]
                        }

                        currentAttempt = {
                            ...currentAttempt,

                            answers:
                                nextAnswers,

                            totalScore:
                                Number(
                                    responsePayload
                                        ?.totalScore ??
                                    nextAnswers.reduce(
                                        (
                                            total,
                                            answer,
                                        ) =>
                                            total +
                                            Number(
                                                answer
                                                    ?.points ||
                                                0,
                                            ),
                                        0,
                                    ),
                                ),

                            answeredQuestions:
                                Number(
                                    responsePayload
                                        ?.answeredQuestions ??
                                    nextAnswers.length,
                                ),

                            offline:
                                false,
                        }

                        setAttempt(
                            currentAttempt,
                        )

                        console.log(
                            '[QUIZ] Resposta online espelhada localmente:',
                            {
                                attemptId:
                                    currentAttempt.id,

                                answeredQuestions:
                                    currentAttempt
                                        .answeredQuestions,

                                totalScore:
                                    currentAttempt
                                        .totalScore,
                            },
                        )
                    } catch (
                    onlineAnswerError
                    ) {
                        // ==================================
                        // ERRO HTTP NAO VIRA OFFLINE
                        // ==================================

                        const status =
                            onlineAnswerError
                                ?.response
                                ?.status

                        if (
                            status &&
                            status >=
                            400 &&
                            status <
                            500
                        ) {
                            throw onlineAnswerError
                        }

                        // ==================================
                        // QUEDA DE REDE:
                        // ADOTAR TENTATIVA ONLINE
                        // ==================================

                        console.warn(
                            '[QUIZ OFFLINE] Conexao perdida durante a tentativa. Continuando localmente.',
                            onlineAnswerError,
                        )

                        const optionPoints =
                            Number(
                                option.points ||
                                option.score ||
                                option.value ||
                                0,
                            )

                        const adoptedAttempt =
                            await OfflineQuizAttemptService
                                .adoptOnlineAttempt({
                                    serverAttemptId:
                                        currentAttempt.id,

                                    gameId:
                                        game.id,

                                    leadId:
                                        currentAttempt
                                            .leadId ||
                                        null,

                                    participant:
                                        currentAttempt
                                            .participant ||
                                        participant ||
                                        null,

                                    answers:
                                        Array.isArray(
                                            currentAttempt
                                                .answers,
                                        )
                                            ? currentAttempt
                                                .answers
                                            : [],

                                    totalScore:
                                        currentAttempt
                                            .totalScore ||
                                        0,

                                    answeredQuestions:
                                        currentAttempt
                                            .answeredQuestions ||
                                        0,

                                    startedAt:
                                        currentAttempt
                                            .startedAt ||
                                        null,
                                })

                        if (
                            !adoptedAttempt?.id
                        ) {
                            throw new Error(
                                'QUIZ_OFFLINE_ADOPTION_FAILED',
                            )
                        }

                        // ==================================
                        // SALVAR A RESPOSTA QUE FALHOU
                        // ==================================

                        const updatedAttempt =
                            await OfflineQuizAttemptService
                                .saveAnswer({
                                    attemptId:
                                        adoptedAttempt.id,

                                    questionId,

                                    optionId,

                                    points:
                                        optionPoints,
                                })

                        currentAttempt = {
                            ...updatedAttempt,

                            offline:
                                true,
                        }

                        setAttempt(
                            currentAttempt,
                        )

                        console.log(
                            '[QUIZ OFFLINE] Tentativa online adotada e resposta salva localmente:',
                            {
                                localAttemptId:
                                    currentAttempt.id,

                                serverAttemptId:
                                    currentAttempt
                                        .serverAttemptId,

                                questionId,

                                optionId,

                                totalScore:
                                    currentAttempt
                                        .totalScore,
                            },
                        )
                    }
                }

                // ==========================================
                // ÚLTIMA PERGUNTA
                // ==========================================

                const isLast =
                    questionIndex >=
                    questions.length -
                    1

                if (
                    isLast
                ) {
                    await finishAttempt(
                        currentAttempt.id,
                    )

                    return
                }

                // ==========================================
                // AVANÇAR
                // ==========================================

                const advance =
                    () => {
                        setQuestionIndex(
                            (
                                current,
                            ) =>
                                current +
                                1,
                        )

                        setSelectedOptionId(
                            null,
                        )

                        advanceTimerRef.current =
                            null

                        answeringRef.current =
                            false

                        setSubmitting(
                            false,
                        )
                    }

                if (
                    config
                        ?.autoAdvance !==
                    false
                ) {
                    const delay =
                        Math.max(
                            0,
                            Number(
                                config
                                    ?.autoAdvanceDelayMs ||
                                0,
                            ),
                        )

                    advanceTimerRef.current =
                        window.setTimeout(
                            advance,
                            delay,
                        )

                    // IMPORTANTE:
                    // não liberar a trava no finally.
                    return
                }

                advance()
            } catch (
            requestError
            ) {
                console.error(
                    '[QUIZ] Erro ao responder:',
                    requestError,
                )

                setSelectedOptionId(
                    null,
                )

                answeringRef.current =
                    false

                setSubmitting(
                    false,
                )

                setError(
                    requestError
                        ?.response
                        ?.data
                        ?.message ||
                    requestError
                        ?.message ||
                    'Não foi possível registrar a resposta.',
                )
            }
        }
    // ==================================================
    // FINALIZAR EXPERIÊNCIA
    // ==================================================

    const handleFinish =
        () => {
            // ==========================================
            // CANCELAR TIMER PENDENTE
            // ==========================================

            if (
                advanceTimerRef.current
            ) {
                window.clearTimeout(
                    advanceTimerRef.current,
                )

                advanceTimerRef.current =
                    null
            }

            // ==========================================
            // LIBERAR TRAVAS
            // ==========================================

            answeringRef.current =
                false

            startingRef.current =
                false

            setSubmitting(
                false,
            )

            // ==========================================
            // FINALIZAR NO COMPONENTE PAI
            // ==========================================

            if (
                typeof onGameFinished ===
                'function'
            ) {
                onGameFinished(
                    result,
                )

                return
            }

            // ==========================================
            // RESET COMPLETO
            // ==========================================

            setAttempt(
                null,
            )

            setResult(
                null,
            )

            setQuestionIndex(
                0,
            )

            setSelectedOptionId(
                null,
            )

            setError(
                '',
            )

            setScreen(
                config
                    ?.startScreenEnabled ===
                    false
                    ? 'QUESTION'
                    : 'START',
            )
        }

    // ==================================================
    // LOADING
    // ==================================================

    if (loading) {
        return (
            <div
                style={{
                    width:
                        '100vw',
                    height:
                        '100vh',
                    display:
                        'flex',
                    alignItems:
                        'center',
                    justifyContent:
                        'center',
                    background:
                        '#000000',
                }}
            >
                <Spin
                    size="large"
                />
            </div>
        )
    }

    if (
        !config
    ) {
        return (
            <div
                style={{
                    minHeight:
                        '100vh',
                    padding:
                        30,
                    background:
                        '#000000',
                }}
            >
                <Alert
                    type="error"
                    showIcon
                    message={
                        error ||
                        'Quiz indisponível.'
                    }
                />
            </div>
        )
    }

    // ==================================================
    // DIMENSÕES
    // ==================================================

    const orientation =
        config.orientation ||
        'VERTICAL'

    const horizontal =
        orientation ===
        'HORIZONTAL'

    const designWidth =
        Number(
            config.screenWidth,
        ) ||
        (
            horizontal
                ? 1920
                : 1080
        )

    const designHeight =
        Number(
            config.screenHeight,
        ) ||
        (
            horizontal
                ? 1080
                : 1920
        )

    const rootStyle = {
        position:
            'relative',

        width:
            '100vw',

        height:
            '100vh',

        minWidth:
            0,

        overflow:
            'hidden',

        fontFamily:
            config.fontFamily ||
            'Arial',

        background:
            '#000000',
    }

    // ==================================================
    // ESCALA RESPONSIVA
    // ==================================================

    const viewportWidth =
        typeof window !==
            'undefined'
            ? window.innerWidth
            : designWidth

    const viewportHeight =
        typeof window !==
            'undefined'
            ? window.innerHeight
            : designHeight

    const scaleX =
        viewportWidth /
        designWidth

    const scaleY =
        viewportHeight /
        designHeight

    /*
     * COVER
     *
     * O palco sempre ocupa toda a viewport.
     *
     * Como o Quiz normalmente será executado na mesma
     * proporção configurada no painel:
     *
     * Horizontal: 1920 x 1080
     * Vertical:   1080 x 1920
     *
     * em fullscreen não haverá recorte.
     *
     * Caso a proporção da janela seja diferente,
     * haverá um pequeno corte nas extremidades,
     * em vez de barras pretas.
     */
    const scale =
        Math.max(
            scaleX,
            scaleY,
        )

    const stageWidth =
        designWidth *
        scale

    const stageHeight =
        designHeight *
        scale

    const stageStyle = {
        position:
            'absolute',

        width:
            designWidth,

        height:
            designHeight,

        left:
            '50%',

        top:
            '50%',

        transform:
            `translate(-50%, -50%) scale(${scale})`,

        transformOrigin:
            'center center',

        overflow:
            'hidden',
    }

    // ==================================================
    // ERRO FLUTUANTE
    // ==================================================

    const renderError =
        error
            ? (
                <div
                    style={{
                        position:
                            'absolute',
                        zIndex:
                            100,
                        left:
                            40,
                        right:
                            40,
                        top:
                            30,
                    }}
                >
                    <Alert
                        closable
                        type="error"
                        showIcon
                        message={
                            error
                        }
                        onClose={() =>
                            setError(
                                '',
                            )
                        }
                    />
                </div>
            )
            : null

    // ==================================================
    // START
    // ==================================================

    if (
        screen ===
        'START'
    ) {
        const cardWidth =
            Number(
                config
                    .startScreenCardWidth,
            ) ||
            (
                horizontal
                    ? 760
                    : 820
            )

        const contentPosition =
            config
                .startScreenContentPosition ||
            config
                .startScreenCardPosition ||
            'LEFT'

        const contentVerticalPosition =
            config
                .startScreenContentVerticalPosition ||
            'CENTER'

        const contentOffsetX =
            Number(
                config
                    .startScreenContentOffsetX,
            ) ||
            0

        const contentOffsetY =
            Number(
                config
                    .startScreenContentOffsetY,
            ) ||
            0

        const textAlign =
            config
                .startScreenTextAlign ||
            (
                contentPosition ===
                    'RIGHT'
                    ? 'RIGHT'
                    : contentPosition ===
                        'CENTER'
                        ? 'CENTER'
                        : 'LEFT'
            )

        const buttonPositionMode =
            config
                .startScreenButtonPositionMode ||
            'FLOW'

        const buttonAlign =
            config
                .startScreenButtonAlign ||
            textAlign

        const buttonX =
            Number(
                config
                    .startScreenButtonPositionX,
            ) ||
            0

        const buttonY =
            Number(
                config
                    .startScreenButtonPositionY,
            ) ||
            0

        const buttonWidth =
            Number(
                config
                    .startScreenButtonWidth,
            ) ||
            300

        const buttonHeight =
            Number(
                config
                    .startScreenButtonHeight,
            ) ||
            72

        const buttonStyle = {
            width:
                buttonWidth,

            height:
                buttonHeight,

            border:
                'none',

            borderRadius:
                Number(
                    config
                        .startScreenButtonBorderRadius,
                ) ||
                18,

            background:
                config
                    .startScreenPrimaryColor ||
                '#e5cf70',

            color:
                config
                    .startScreenButtonTextColor ||
                '#000000',

            fontSize:
                Number(
                    config
                        .startScreenButtonFontSize,
                ) ||
                28,

            fontWeight:
                800,

            fontFamily:
                'inherit',

            display:
                'flex',

            alignItems:
                'center',

            justifyContent:
                'center',

            padding:
                0,
        }

        const startButton = (
            <Button
                type="primary"
                size="large"
                loading={
                    submitting
                }
                onClick={
                    handleStart
                }
                style={
                    buttonStyle
                }
            >
                {config
                    .startScreenButtonText ||
                    'COMEÇAR'}
            </Button>
        )

        return (
            <div
                style={
                    rootStyle
                }
            >
                <div
                    style={{
                        position:
                            'absolute',

                        width:
                            stageWidth,

                        height:
                            stageHeight,

                        left:
                            '50%',

                        top:
                            '50%',

                        transform:
                            'translate(-50%, -50%)',

                        overflow:
                            'hidden',
                    }}
                >
                    <div
                        style={
                            stageStyle
                        }
                    >
                        <ScreenBackground
                            mediaType={
                                config
                                    .startScreenMediaType
                            }
                            imageUrl={
                                config
                                    .startScreenBackgroundUrl
                            }
                            videoUrl={
                                config
                                    .startScreenBackgroundVideoUrl
                            }
                            backgroundColor={
                                config
                                    .startScreenBackgroundColor
                            }
                        />

                        <ScreenOverlay
                            enabled={
                                config
                                    .startScreenOverlayEnabled
                            }
                            color={
                                config
                                    .startScreenOverlayColor
                            }
                        />

                        {renderError}

                        {/* ================================= */}
                        {/* CONTEÚDO PRINCIPAL */}
                        {/* ================================= */}

                        <div
                            style={{
                                position:
                                    'absolute',

                                zIndex:
                                    10,

                                inset:
                                    0,

                                display:
                                    'flex',

                                alignItems:
                                    getVerticalAlignment(
                                        contentVerticalPosition,
                                    ),

                                ...getHorizontalAlignment(
                                    contentPosition,
                                ),

                                padding:
                                    horizontal
                                        ? '70px 100px'
                                        : '100px 70px',

                                boxSizing:
                                    'border-box',

                                pointerEvents:
                                    'none',
                            }}
                        >
                            <div
                                style={{
                                    width:
                                        cardWidth,

                                    maxWidth:
                                        '90%',

                                    padding:
                                        Number(
                                            config
                                                .startScreenCardPadding,
                                        ) ||
                                        50,

                                    boxSizing:
                                        'border-box',

                                    background:
                                        config
                                            .startScreenCardEnabled ===
                                            false
                                            ? 'transparent'
                                            : (
                                                config
                                                    .startScreenCardBackground ||
                                                'rgba(0,0,0,0.65)'
                                            ),

                                    border:
                                        config
                                            .startScreenCardEnabled ===
                                            false
                                            ? 'none'
                                            : `${Number(
                                                config
                                                    .startScreenCardBorderWidth ||
                                                0,
                                            )}px solid ${config
                                                .startScreenCardBorderColor ||
                                            'transparent'
                                            }`,

                                    borderRadius:
                                        Number(
                                            config
                                                .startScreenCardBorderRadius,
                                        ) ||
                                        0,

                                    textAlign:
                                        String(
                                            textAlign,
                                        ).toLowerCase(),

                                    transform:
                                        `translate(${contentOffsetX}px, ${contentOffsetY}px)`,

                                    pointerEvents:
                                        'auto',
                                }}
                            >
                                <QuizLogo
                                    show={
                                        config
                                            .startScreenShowLogo
                                    }
                                    url={
                                        config
                                            .startScreenLogoUrl
                                    }
                                    width={
                                        config
                                            .startScreenLogoWidth
                                    }
                                />

                                {config
                                    .startScreenShowTitle !==
                                    false && (
                                        <div
                                            style={{
                                                color:
                                                    config
                                                        .startScreenTitleColor ||
                                                    config
                                                        .startScreenTextColor ||
                                                    '#ffffff',

                                                fontSize:
                                                    Number(
                                                        config
                                                            .startScreenTitleFontSize,
                                                    ) ||
                                                    70,

                                                fontWeight:
                                                    800,

                                                lineHeight:
                                                    1.05,

                                                marginBottom:
                                                    25,
                                            }}
                                        >
                                            {config
                                                .startScreenTitle ||
                                                'QUIZ'}
                                        </div>
                                    )}

                                {config
                                    .startScreenShowSubtitle !==
                                    false &&
                                    config
                                        .startScreenSubtitle && (
                                        <div
                                            style={{
                                                color:
                                                    config
                                                        .startScreenSubtitleColor ||
                                                    config
                                                        .startScreenTextColor ||
                                                    '#ffffff',

                                                fontSize:
                                                    Number(
                                                        config
                                                            .startScreenSubtitleFontSize,
                                                    ) ||
                                                    34,

                                                lineHeight:
                                                    1.35,

                                                marginBottom:
                                                    buttonPositionMode ===
                                                        'FLOW'
                                                        ? 40
                                                        : 0,
                                            }}
                                        >
                                            {
                                                config
                                                    .startScreenSubtitle
                                            }
                                        </div>
                                    )}

                                {/* ============================= */}
                                {/* BOTÃO NO FLUXO */}
                                {/* ============================= */}

                                {buttonPositionMode ===
                                    'FLOW' && (
                                        <div
                                            style={{
                                                width:
                                                    '100%',

                                                display:
                                                    'flex',

                                                justifyContent:
                                                    getButtonAlignment(
                                                        buttonAlign,
                                                    ),
                                            }}
                                        >
                                            {startButton}
                                        </div>
                                    )}
                            </div>
                        </div>

                        {/* ================================= */}
                        {/* BOTÃO LIVRE */}
                        {/* ================================= */}

                        {buttonPositionMode ===
                            'FREE' && (
                                <div
                                    style={{
                                        position:
                                            'absolute',

                                        zIndex:
                                            30,

                                        left:
                                            buttonX,

                                        top:
                                            buttonY,
                                    }}
                                >
                                    {startButton}
                                </div>
                            )}
                    </div>
                </div>
            </div>
        )
    }

    // ==================================================
    // PERGUNTA
    // ==================================================

    if (
        screen ===
        'QUESTION'
    ) {
        if (
            !currentQuestion
        ) {
            return (
                <div
                    style={
                        rootStyle
                    }
                >
                    <Alert
                        type="warning"
                        showIcon
                        message="Nenhuma pergunta ativa encontrada."
                    />
                </div>
            )
        }

        // ==================================================
        // PROGRESSO
        // ==================================================

        const progress =
            questions.length
                ? Math.round(
                    (
                        (
                            questionIndex +
                            1
                        ) /
                        questions.length
                    ) *
                    100,
                )
                : 0

        // ==================================================
        // POSIÇÃO DO CARD
        // ==================================================

        const questionPosition =
            config
                .questionCardPosition ||
            'LEFT'

        const questionJustifyContent =
            questionPosition ===
                'RIGHT'
                ? 'flex-end'
                : questionPosition ===
                    'CENTER'
                    ? 'center'
                    : 'flex-start'

        // ==================================================
        // CONFIGURAÇÕES VISUAIS
        // ==================================================

        const questionCardWidth =
            Number(
                config
                    .questionCardWidth,
            ) ||
            900

        const questionCardPadding =
            Number(
                config
                    .questionCardPadding,
            ) ||
            50

        const questionCounterFontSize =
            Number(
                config
                    .questionCounterFontSize,
            ) ||
            24

        const progressHeight =
            Number(
                config
                    .progressHeight,
            ) ||
            12

        const progressBorderRadius =
            Number(
                config
                    .progressBorderRadius,
            ) ||
            100

        const optionGap =
            Number(
                config
                    .optionGap,
            ) ||
            16

        const optionHeight =
            Number(
                config
                    .optionHeight,
            ) ||
            76

        const optionFontSize =
            Number(
                config
                    .optionFontSize,
            ) ||
            25

        const optionBorderWidth =
            Number(
                config
                    .optionBorderWidth,
            ) ||
            1

        const optionBorderRadius =
            Number(
                config
                    .optionBorderRadius,
            ) ||
            15

        return (
            <div
                style={
                    rootStyle
                }
            >
                <div
                    style={{
                        position:
                            'absolute',

                        width:
                            stageWidth,

                        height:
                            stageHeight,

                        left:
                            '50%',

                        top:
                            '50%',

                        transform:
                            'translate(-50%, -50%)',

                        overflow:
                            'hidden',
                    }}
                >
                    <div
                        style={
                            stageStyle
                        }
                    >
                        {/* ================================= */}
                        {/* FUNDO */}
                        {/* ================================= */}

                        <ScreenBackground
                            mediaType={
                                config
                                    .questionMediaType
                            }
                            imageUrl={
                                currentQuestion
                                    .backgroundUrl ||
                                config
                                    .questionBackgroundUrl
                            }
                            videoUrl={
                                config
                                    .questionBackgroundVideoUrl
                            }
                            backgroundColor={
                                config
                                    .questionBackgroundColor
                            }
                        />

                        {/* ================================= */}
                        {/* OVERLAY */}
                        {/* ================================= */}

                        <ScreenOverlay
                            enabled={
                                config
                                    .questionOverlayEnabled
                            }
                            color={
                                config
                                    .questionOverlayColor
                            }
                        />

                        {renderError}

                        {/* ================================= */}
                        {/* ÁREA DA PERGUNTA */}
                        {/* ================================= */}

                        <div
                            style={{
                                position:
                                    'relative',

                                zIndex:
                                    10,

                                width:
                                    '100%',

                                height:
                                    '100%',

                                padding:
                                    horizontal
                                        ? '55px 80px'
                                        : '90px 60px',

                                boxSizing:
                                    'border-box',

                                display:
                                    'flex',

                                alignItems:
                                    'center',

                                justifyContent:
                                    questionJustifyContent,
                            }}
                        >
                            {/* ============================= */}
                            {/* CARD */}
                            {/* ============================= */}

                            <div
                                style={{
                                    width:
                                        questionCardWidth,

                                    maxWidth:
                                        '92%',

                                    boxSizing:
                                        'border-box',

                                    padding:
                                        questionCardPadding,

                                    background:
                                        config
                                            .questionCardBackground ||
                                        'rgba(0,0,0,0.80)',

                                    border:
                                        `${Number(
                                            config
                                                .questionCardBorderWidth ||
                                            0,
                                        )}px solid ${config
                                            .questionCardBorderColor ||
                                        'transparent'
                                        }`,

                                    borderRadius:
                                        Number(
                                            config
                                                .questionCardBorderRadius,
                                        ) ||
                                        0,
                                }}
                            >
                                {/* ========================= */}
                                {/* LOGO */}
                                {/* ========================= */}

                                <QuizLogo
                                    show={
                                        config
                                            .questionShowLogo
                                    }
                                    url={
                                        config
                                            .questionLogoUrl
                                    }
                                    width={
                                        config
                                            .questionLogoWidth
                                    }
                                />

                                {/* ========================= */}
                                {/* CONTADOR */}
                                {/* ========================= */}

                                {config
                                    .showQuestionCounter !==
                                    false && (
                                        <div
                                            style={{
                                                color:
                                                    config
                                                        .questionCounterColor ||
                                                    config
                                                        .questionTitleColor ||
                                                    '#e5cf70',

                                                fontSize:
                                                    questionCounterFontSize,

                                                fontWeight:
                                                    700,

                                                marginBottom:
                                                    14,
                                            }}
                                        >
                                            Pergunta{' '}
                                            {questionIndex +
                                                1}{' '}
                                            de{' '}
                                            {
                                                questions.length
                                            }
                                        </div>
                                    )}

                                {/* ========================= */}
                                {/* PROGRESSO */}
                                {/* ========================= */}

                                {config
                                    .showProgress !==
                                    false && (
                                        <div
                                            style={{
                                                width:
                                                    '100%',

                                                height:
                                                    progressHeight,

                                                background:
                                                    config
                                                        .progressBackgroundColor ||
                                                    'rgba(255,255,255,0.20)',

                                                borderRadius:
                                                    progressBorderRadius,

                                                overflow:
                                                    'hidden',

                                                marginBottom:
                                                    20,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width:
                                                        `${progress}%`,

                                                    height:
                                                        '100%',

                                                    background:
                                                        config
                                                            .progressColor ||
                                                        config
                                                            .optionSelectedColor ||
                                                        '#e5cf70',

                                                    borderRadius:
                                                        progressBorderRadius,

                                                    transition:
                                                        'width 250ms ease',
                                                }}
                                            />
                                        </div>
                                    )}

                                {/* ========================= */}
                                {/* SCORE */}
                                {/* ========================= */}

                                {config
                                    .showScore ===
                                    true && (
                                        <div
                                            style={{
                                                color:
                                                    config
                                                        .scoreColor ||
                                                    '#ffffff',

                                                fontSize:
                                                    Number(
                                                        config
                                                            .scoreFontSize,
                                                    ) ||
                                                    24,

                                                fontWeight:
                                                    700,

                                                marginBottom:
                                                    14,
                                            }}
                                        >
                                            Pontos:{' '}
                                            {totalScore ||
                                                0}
                                        </div>
                                    )}

                                {/* ========================= */}
                                {/* TÍTULO */}
                                {/* ========================= */}

                                <div
                                    style={{
                                        color:
                                            config
                                                .questionTitleColor ||
                                            '#e5cf70',

                                        fontSize:
                                            Number(
                                                config
                                                    .questionTitleFontSize,
                                            ) ||
                                            42,

                                        fontWeight:
                                            800,

                                        lineHeight:
                                            1.15,

                                        marginBottom:
                                            12,
                                    }}
                                >
                                    {
                                        currentQuestion
                                            .title
                                    }
                                </div>

                                {/* ========================= */}
                                {/* TEXTO */}
                                {/* ========================= */}

                                <div
                                    style={{
                                        color:
                                            config
                                                .questionTextColor ||
                                            '#ffffff',

                                        fontSize:
                                            Number(
                                                config
                                                    .questionTextFontSize,
                                            ) ||
                                            31,

                                        lineHeight:
                                            1.3,

                                        marginBottom:
                                            30,
                                    }}
                                >
                                    {
                                        currentQuestion
                                            .text
                                    }
                                </div>

                                {/* ========================= */}
                                {/* ALTERNATIVAS */}
                                {/* ========================= */}

                                <div
                                    style={{
                                        display:
                                            'grid',

                                        gridTemplateColumns:
                                            '1fr',

                                        gap:
                                            optionGap,
                                    }}
                                >
                                    {options.map(
                                        (
                                            option,
                                        ) => {
                                            const selected =
                                                selectedOptionId ===
                                                option.id

                                            const optionBackground =
                                                selected
                                                    ? (
                                                        config
                                                            .optionSelectedBackground ||
                                                        config
                                                            .optionSelectedColor ||
                                                        '#e5cf70'
                                                    )
                                                    : (
                                                        config
                                                            .optionBackground ||
                                                        'rgba(0,0,0,0.35)'
                                                    )

                                            const optionTextColor =
                                                selected
                                                    ? (
                                                        config
                                                            .optionSelectedTextColor ||
                                                        '#000000'
                                                    )
                                                    : (
                                                        config
                                                            .optionTextColor ||
                                                        '#ffffff'
                                                    )

                                            const optionBorderColor =
                                                selected
                                                    ? (
                                                        config
                                                            .optionSelectedBorderColor ||
                                                        config
                                                            .optionSelectedColor ||
                                                        '#e5cf70'
                                                    )
                                                    : (
                                                        config
                                                            .optionBorderColor ||
                                                        '#ffffff'
                                                    )

                                            return (
                                                <button
                                                    key={
                                                        option.id
                                                    }
                                                    type="button"
                                                    disabled={
                                                        submitting
                                                    }
                                                    onClick={() =>
                                                        handleAnswer(
                                                            option,
                                                        )
                                                    }
                                                    style={{
                                                        width:
                                                            '100%',

                                                        minHeight:
                                                            optionHeight,

                                                        padding:
                                                            '14px 22px',

                                                        display:
                                                            'flex',

                                                        alignItems:
                                                            'center',

                                                        gap:
                                                            18,

                                                        textAlign:
                                                            'left',

                                                        cursor:
                                                            submitting
                                                                ? 'wait'
                                                                : 'pointer',

                                                        background:
                                                            optionBackground,

                                                        color:
                                                            optionTextColor,

                                                        border:
                                                            `${optionBorderWidth}px solid ${optionBorderColor}`,

                                                        borderRadius:
                                                            optionBorderRadius,

                                                        fontSize:
                                                            optionFontSize,

                                                        fontFamily:
                                                            'inherit',

                                                        transition:
                                                            'all 180ms ease',

                                                        boxSizing:
                                                            'border-box',
                                                    }}
                                                >
                                                    {/* ================= */}
                                                    {/* LETRA A / B / C */}
                                                    {/* ================= */}

                                                    <span
                                                        style={{
                                                            flex:
                                                                '0 0 auto',

                                                            minWidth:
                                                                48,

                                                            height:
                                                                48,

                                                            padding:
                                                                '0 10px',

                                                            borderRadius:
                                                                12,

                                                            display:
                                                                'flex',

                                                            alignItems:
                                                                'center',

                                                            justifyContent:
                                                                'center',

                                                            background:
                                                                config
                                                                    .optionLabelBackground ||
                                                                'rgba(255,255,255,0.15)',

                                                            color:
                                                                config
                                                                    .optionLabelTextColor ||
                                                                optionTextColor,

                                                            fontWeight:
                                                                900,

                                                            lineHeight:
                                                                1,
                                                        }}
                                                    >
                                                        {
                                                            option.label
                                                        }
                                                    </span>

                                                    {/* ================= */}
                                                    {/* TEXTO */}
                                                    {/* ================= */}

                                                    <span
                                                        style={{
                                                            flex:
                                                                1,

                                                            lineHeight:
                                                                1.25,
                                                        }}
                                                    >
                                                        {
                                                            option.text
                                                        }
                                                    </span>
                                                </button>
                                            )
                                        },
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // ==================================================
    // RESULTADO
    // ==================================================

    const resultRange =
        result?.resultRange ||
        result?.result ||
        result?.range ||
        null

    const totalScore =
        result?.totalScore ??
        result?.attempt
            ?.totalScore ??
        attempt?.totalScore ??
        0

    // ==================================================
    // POSIÇÃO DO CARD
    // ==================================================

    const resultPosition =
        config
            .resultCardPosition ||
        'CENTER'

    const resultJustifyContent =
        resultPosition ===
            'RIGHT'
            ? 'flex-end'
            : resultPosition ===
                'LEFT'
                ? 'flex-start'
                : 'center'

    // ==================================================
    // DIMENSÕES
    // ==================================================

    const resultCardWidth =
        Number(
            config
                .resultCardWidth,
        ) ||
        (
            horizontal
                ? 850
                : 900
        )

    const resultCardPadding =
        Number(
            config
                .resultCardPadding,
        ) ||
        50

    const resultTextFontSize =
        Number(
            config
                .resultTextFontSize,
        ) ||
        25

    const resultButtonWidth =
        Number(
            config
                .resultButtonWidth,
        ) ||
        300

    const resultButtonHeight =
        Number(
            config
                .resultButtonHeight,
        ) ||
        70

    return (
        <div
            style={
                rootStyle
            }
        >
            <div
                style={{
                    position:
                        'absolute',

                    width:
                        stageWidth,

                    height:
                        stageHeight,

                    left:
                        '50%',

                    top:
                        '50%',

                    transform:
                        'translate(-50%, -50%)',

                    overflow:
                        'hidden',
                }}
            >
                <div
                    style={
                        stageStyle
                    }
                >
                    {/* ================================= */}
                    {/* FUNDO */}
                    {/* ================================= */}

                    <ScreenBackground
                        mediaType={
                            config
                                .resultMediaType
                        }
                        imageUrl={
                            config
                                .resultBackgroundUrl
                        }
                        videoUrl={
                            config
                                .resultBackgroundVideoUrl
                        }
                        backgroundColor={
                            config
                                .resultBackgroundColor
                        }
                    />

                    {/* ================================= */}
                    {/* OVERLAY */}
                    {/* ================================= */}

                    <ScreenOverlay
                        enabled={
                            config
                                .resultOverlayEnabled
                        }
                        color={
                            config
                                .resultOverlayColor
                        }
                    />

                    {renderError}

                    {/* ================================= */}
                    {/* ÁREA DO RESULTADO */}
                    {/* ================================= */}

                    <div
                        style={{
                            position:
                                'relative',

                            zIndex:
                                10,

                            width:
                                '100%',

                            height:
                                '100%',

                            display:
                                'flex',

                            alignItems:
                                'center',

                            justifyContent:
                                resultJustifyContent,

                            padding:
                                horizontal
                                    ? '60px 90px'
                                    : '100px 60px',

                            boxSizing:
                                'border-box',
                        }}
                    >
                        {/* ============================= */}
                        {/* CARD */}
                        {/* ============================= */}

                        <div
                            style={{
                                width:
                                    resultCardWidth,

                                maxWidth:
                                    '92%',

                                padding:
                                    resultCardPadding,

                                boxSizing:
                                    'border-box',

                                textAlign:
                                    'center',

                                background:
                                    config
                                        .resultCardBackground ||
                                    '#000000',

                                border:
                                    `${Number(
                                        config
                                            .resultCardBorderWidth ||
                                        0,
                                    )}px solid ${config
                                        .resultCardBorderColor ||
                                    'transparent'
                                    }`,

                                borderRadius:
                                    Number(
                                        config
                                            .resultCardBorderRadius,
                                    ) ||
                                    0,
                            }}
                        >
                            {/* ========================= */}
                            {/* LOGO */}
                            {/* ========================= */}

                            <QuizLogo
                                show={
                                    config
                                        .resultShowLogo
                                }
                                url={
                                    config
                                        .resultLogoUrl
                                }
                                width={
                                    config
                                        .resultLogoWidth
                                }
                            />

                            {/* ========================= */}
                            {/* TÍTULO */}
                            {/* ========================= */}

                            <div
                                style={{
                                    color:
                                        config
                                            .resultTitleColor ||
                                        '#e5cf70',

                                    fontSize:
                                        Number(
                                            config
                                                .resultTitleFontSize,
                                        ) ||
                                        52,

                                    fontWeight:
                                        800,

                                    lineHeight:
                                        1.1,

                                    marginBottom:
                                        20,
                                }}
                            >
                                {resultRange
                                    ?.title ||
                                    config
                                        .resultTitleText ||
                                    'RESULTADO'}
                            </div>

                            {/* ========================= */}
                            {/* SUBTÍTULO */}
                            {/* ========================= */}

                            {resultRange
                                ?.subtitle && (
                                    <div
                                        style={{
                                            color:
                                                config
                                                    .resultTextColor ||
                                                '#ffffff',

                                            fontSize:
                                                resultTextFontSize,

                                            fontWeight:
                                                700,

                                            lineHeight:
                                                1.3,

                                            marginBottom:
                                                20,
                                        }}
                                    >
                                        {
                                            resultRange
                                                .subtitle
                                        }
                                    </div>
                                )}

                            {/* ========================= */}
                            {/* SCORE */}
                            {/* ========================= */}

                            {config
                                .resultShowScore ===
                                true && (
                                    <div
                                        style={{
                                            color:
                                                config
                                                    .resultScoreColor ||
                                                config
                                                    .resultTextColor ||
                                                '#ffffff',

                                            fontSize:
                                                Number(
                                                    config
                                                        .resultScoreFontSize,
                                                ) ||
                                                32,

                                            fontWeight:
                                                800,

                                            marginBottom:
                                                20,
                                        }}
                                    >
                                        Pontuação:{' '}

                                        <strong>
                                            {
                                                totalScore
                                            }
                                        </strong>
                                    </div>
                                )}

                            {/* ========================= */}
                            {/* DESCRIÇÃO */}
                            {/* ========================= */}

                            {resultRange
                                ?.description && (
                                    <div
                                        style={{
                                            color:
                                                config
                                                    .resultTextColor ||
                                                '#ffffff',

                                            fontSize:
                                                resultTextFontSize,

                                            lineHeight:
                                                1.45,

                                            whiteSpace:
                                                'pre-line',

                                            marginBottom:
                                                25,
                                        }}
                                    >
                                        {
                                            resultRange
                                                .description
                                        }
                                    </div>
                                )}

                            {/* ========================= */}
                            {/* RECOMENDAÇÃO */}
                            {/* ========================= */}

                            {resultRange
                                ?.recommendation && (
                                    <div
                                        style={{
                                            color:
                                                config
                                                    .resultTextColor ||
                                                '#ffffff',

                                            fontSize:
                                                resultTextFontSize,

                                            lineHeight:
                                                1.45,

                                            whiteSpace:
                                                'pre-line',

                                            marginBottom:
                                                35,
                                        }}
                                    >
                                        {
                                            resultRange
                                                .recommendation
                                        }
                                    </div>
                                )}

                            {/* ========================= */}
                            {/* IMAGEM DO RESULTADO */}
                            {/* ========================= */}

                            {config
                                .resultShowImage !==
                                false &&
                                resultRange
                                    ?.imageUrl && (
                                    <img
                                        src={
                                            getMediaUrl(
                                                resultRange
                                                    .imageUrl,
                                            )
                                        }
                                        alt=""
                                        style={{
                                            display:
                                                'block',

                                            width:
                                                Number(
                                                    config
                                                        .resultImageWidth,
                                                ) ||
                                                'auto',

                                            maxWidth:
                                                '100%',

                                            height:
                                                'auto',

                                            maxHeight:
                                                320,

                                            objectFit:
                                                'contain',

                                            margin:
                                                '0 auto 30px',

                                            borderRadius:
                                                Number(
                                                    config
                                                        .resultImageBorderRadius,
                                                ) ||
                                                0,
                                        }}
                                    />
                                )}

                            {/* ========================= */}
                            {/* BOTÃO */}
                            {/* ========================= */}

                            <div
                                style={{
                                    width:
                                        '100%',

                                    display:
                                        'flex',

                                    justifyContent:
                                        'center',
                                }}
                            >
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={
                                        handleFinish
                                    }
                                    style={{
                                        width:
                                            resultButtonWidth,

                                        height:
                                            resultButtonHeight,

                                        padding:
                                            0,

                                        border:
                                            'none',

                                        borderRadius:
                                            Number(
                                                config
                                                    .resultButtonBorderRadius,
                                            ) ||
                                            18,

                                        background:
                                            config
                                                .resultButtonColor ||
                                            '#e5cf70',

                                        color:
                                            config
                                                .resultButtonTextColor ||
                                            '#000000',

                                        fontSize:
                                            Number(
                                                config
                                                    .resultButtonFontSize,
                                            ) ||
                                            26,

                                        fontWeight:
                                            800,

                                        fontFamily:
                                            'inherit',

                                        display:
                                            'flex',

                                        alignItems:
                                            'center',

                                        justifyContent:
                                            'center',
                                    }}
                                >
                                    {config
                                        .resultButtonText ||
                                        'FINALIZAR'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default QuizGame